"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withTimeout = exports.safeJsonParse = exports.safeJsonString = exports.getInstallerUrls = exports.isSafari = exports.isValidURL = exports.throwError = exports.randomUUID = exports.getCurrentPlatform = exports.getWebsocketUrl = exports.isValidTransferSpec = exports.generateErrorBody = exports.errorLog = exports.generatePromiseObjects = void 0;
var constants_1 = require("../constants/constants");
/**
 * Generates promise object that can be resolved or rejected via functions
 *
 * @returns an object containing the promise, the resolver and rejecter
 */
var generatePromiseObjects = function () {
    var resolver;
    var rejecter;
    var promise = new Promise(function (resolve, reject) {
        resolver = resolve;
        rejecter = reject;
    });
    return {
        promise: promise,
        resolver: resolver,
        rejecter: rejecter
    };
};
exports.generatePromiseObjects = generatePromiseObjects;
/**
 * Log errors from Aspera SDK
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 */
var errorLog = function (message, debugData) {
    if (debugData && debugData.code && debugData.message) {
        debugData = {
            code: debugData.code,
            message: debugData.message,
            data: debugData.data
        };
    }
    if (typeof window === 'object') {
        if (!Array.isArray(window.asperaSdkLogs)) {
            window.asperaSdkLogs = [];
        }
        window.asperaSdkLogs.push({ message: message, debugData: debugData });
    }
    console.warn("Aspera SDK: ".concat(message), debugData);
};
exports.errorLog = errorLog;
/**
 * Generate error object for rejecter responses
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 *
 * @returns object containing standardized error response
 */
var generateErrorBody = function (message, debugData) {
    var errorResponse = {
        error: true,
        message: message
    };
    if (debugData && debugData.code && debugData.message) {
        errorResponse.debugData = {
            code: debugData.code,
            message: debugData.message,
            data: debugData.data
        };
    }
    return errorResponse;
};
exports.generateErrorBody = generateErrorBody;
/**
 * Validate if transferSpec is valid for server communication
 *
 * @param transferSpec the transferSpec to test
 *
 * @returns boolean indicating whether supplied transferSpec is valid
 */
var isValidTransferSpec = function (transferSpec) {
    if (transferSpec &&
        typeof transferSpec === 'object' &&
        typeof transferSpec.direction === 'string' &&
        typeof transferSpec.remote_host === 'string' &&
        Array.isArray(transferSpec.paths)) {
        return true;
    }
    return false;
};
exports.isValidTransferSpec = isValidTransferSpec;
/**
 * Returns a string indicating the websocket URL to use for talking to the server
 *
 * @returns a string of the full Websocket URL
 */
var getWebsocketUrl = function (serverUrl) {
    var wsProtocol;
    if (serverUrl.indexOf('http:') === 0) {
        wsProtocol = 'ws';
    }
    else if (serverUrl.indexOf('https:') === 0) {
        wsProtocol = 'wss';
    }
    var url = serverUrl.replace('http://', '//').replace('https://', '//');
    return "".concat(wsProtocol, ":").concat(url);
};
exports.getWebsocketUrl = getWebsocketUrl;
/**
 * Simple function to get the current platform.
 *
 * @returns a string indicating the current platform
 */
var getCurrentPlatform = function () {
    var ua = navigator.userAgent;
    if (/Mac/.test(ua)) {
        return 'macos';
    }
    else if (/Win/.test(ua)) {
        return 'windows';
    }
    else if (/Linux/.test(ua)) {
        return 'linux';
    }
    else {
        return 'unknown';
    }
};
exports.getCurrentPlatform = getCurrentPlatform;
/**
 * Function used to create a random UUID
 *
 * @returns string
 */
var randomUUID = function () {
    var _a;
    var fallback = function () {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Number(((new Date().getTime() + 16) * Math.random()).toFixed()) % 16;
            if (c !== 'x') {
                // eslint-disable-next-line no-bitwise
                r = r & 0x3 | 0x8;
            }
            return r.toString(16);
        });
    };
    return ((_a = window.crypto) === null || _a === void 0 ? void 0 : _a.randomUUID) ? window.crypto.randomUUID() : fallback();
};
exports.randomUUID = randomUUID;
/**
 * Return a rejected promise
 *
 * @param message the message indicating the error encountered
 * @param debugData the data with useful debugging information
 *
 * @returns a rejected promise
 */
var throwError = function (message, debugData) {
    (0, exports.errorLog)(message, debugData);
    return new Promise(function (resolve, reject) {
        reject((0, exports.generateErrorBody)(message, debugData));
    });
};
exports.throwError = throwError;
/**
 * Check if the given string is a valid URL
 *
 * @param url string to check if valid URL
 *
 * @returns boolean
 */
var isValidURL = function (url) {
    try {
        new URL(url);
        return true;
    }
    catch (error) {
        return false;
    }
};
exports.isValidURL = isValidURL;
/**
 * Checks if the current browser is Safari.
 * @returns {boolean} Whether the browser is Safari.
 */
var isSafari = function () {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !window.MSStream;
};
exports.isSafari = isSafari;
/**
 * Get the URLs for installer management.
 *
 * @returns Info on URLs where installers live
 */
var getInstallerUrls = function () {
    return {
        base: constants_1.baseInstallerUrl,
        latest: constants_1.installerUrl,
    };
};
exports.getInstallerUrls = getInstallerUrls;
/**
 * Try to stringify a JSON string and log failures
 *
 * @param json - Object to make into a string
 *
 * @returns string representing JSON or empty string on error
 */
var safeJsonString = function (json) {
    try {
        return JSON.stringify(json);
    }
    catch (error) {
        (0, exports.errorLog)('safeJsonString: unable to stringify JSON', { error: error, json: json });
        return '';
    }
};
exports.safeJsonString = safeJsonString;
/**
 * Try to parse a JSON string and log failures
 *
 * @param json - String to make into an object
 *
 * @returns object or array from the JSON string. Or undefined
 */
var safeJsonParse = function (json) {
    if (json && typeof json === 'object') {
        return json;
    }
    try {
        return JSON.parse(json);
    }
    catch (error) {
        (0, exports.errorLog)('safeJsonParse: unable to parse JSON', { error: error, json: json });
        return undefined;
    }
};
exports.safeJsonParse = safeJsonParse;
/**
 * Race a promise against a timeout. Rejects with an Error('timeout') if the
 * given milliseconds elapse before the promise settles.
 */
var withTimeout = function (promise, ms) {
    return new Promise(function (resolve, reject) {
        var timer = setTimeout(function () { return reject(new Error('timeout')); }, ms);
        promise.then(function (val) {
            clearTimeout(timer);
            resolve(val);
        }, function (err) {
            clearTimeout(timer);
            reject(err);
        });
    });
};
exports.withTimeout = withTimeout;
exports.default = {
    errorLog: exports.errorLog,
    generateErrorBody: exports.generateErrorBody,
    generatePromiseObjects: exports.generatePromiseObjects,
    getCurrentPlatform: exports.getCurrentPlatform,
    getWebsocketUrl: exports.getWebsocketUrl,
    isSafari: exports.isSafari,
    isValidURL: exports.isValidURL,
    isValidTransferSpec: exports.isValidTransferSpec,
    randomUUID: exports.randomUUID,
    throwError: exports.throwError,
    getInstallerUrls: exports.getInstallerUrls,
    safeJsonString: exports.safeJsonString,
    safeJsonParse: exports.safeJsonParse,
    withTimeout: exports.withTimeout,
};
